<template>
  <component :is="as" class="card" :class="[tone, { list }]">
    <slot />
  </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'

/** Tags a card can be. Not a button and not a link: a card is a surface, never a control. */
const TAGS = ['div', 'ul', 'ol', 'article', 'section'] as const
export type CardTag = (typeof TAGS)[number]

/**
 * The surface the lists and blocks of 0.1 are built from: the trip's list of items, the verdict
 * card, the «Take» card of «What to buy».
 *
 * The three share only the surface; each has its own tag, padding and edge. The owner chose a
 * component over a mixin (MOL-18, В-1) with that price in view, so it carries exactly the
 * differences between them and nothing for later:
 *
 *   as    — the tag, chosen by the screen, which knows what the content means;
 *   tone  — `take` is the verdict «take» coming from the data: a green edge and a tighter
 *           radius. It is not emphasis and not a place in the results; there is no other tone
 *           and none is to be added;
 *   list  — rows edge to edge with a hairline between them, and no padding of its own.
 *
 * A padding of its own — the verdict card's 24 / 16 — comes from the screen's class, which Vue
 * puts on this root.
 */
export default defineComponent({
  name: 'AppCard',
  props: {
    as: {
      type: String as PropType<CardTag>,
      default: 'div',
      validator: (tag: string) => (TAGS as readonly string[]).includes(tag),
    },
    tone: { type: String as PropType<'plain' | 'take'>, default: 'plain' },
    list: { type: Boolean, default: false },
  },
})
</script>

<style scoped lang="scss">
.card {
  margin: 0;
  padding: var(--space-4);
  border: var(--hairline) solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}

.take {
  border-color: var(--good);
  border-radius: var(--radius);
}

.list {
  /* Its rows turn by its width with large text (`ListRow`, MOL-321). */
  container: row-tight / inline-size;
  padding: 0;
  overflow: hidden;
  list-style: none;

  > :deep(* + *) {
    border-top: var(--hairline) solid var(--border);
  }

  /* The list clips its rows to its radius, and a ring drawn outside a row would be cut on three
     sides — a row that is tapped has to show its focus inside (review Р-4). */
  :deep(:focus-visible) {
    outline-offset: -2px;
  }

  /* Inside, a square ring was cut at the card's corners: «Записать вручную», the one row of its card,
     lost all four (MOL-203, adversarial А1). A row at the card's edge is rounded there while in focus, and
     the ring follows; at its own edges only, so the hairline between rows, a row's `border-top`, stays
     straight. A row may stand in an `li` of its own. Rows of the kit only, by their class: by depth alone
     it rounded a pill «Завершить» into a dome and a line «не знаем» in the middle of a card (round 2, Б1,
     Б2) — neither touches the card's edge. */
  > :deep(:is(.list-row, .nav-row):first-child:focus-visible),
  > :deep(:first-child > :is(.list-row, .nav-row):focus-visible) {
    border-start-start-radius: var(--radius-lg);
    border-start-end-radius: var(--radius-lg);
  }

  > :deep(:is(.list-row, .nav-row):last-child:focus-visible),
  > :deep(:last-child > :is(.list-row, .nav-row):focus-visible) {
    border-end-start-radius: var(--radius-lg);
    border-end-end-radius: var(--radius-lg);
  }
}
</style>
